/**
 * Shared triple-dot ("kebab") overflow menu for Aero cards and rows.
 *
 * The popup is portalled to document.body and positioned from the trigger's
 * bounding rect, so it never clips inside a scrolling card or row. It always
 * carries `ut-theme` on the portalled root so the flat menu styles in
 * `aero-ut-theme.css` (scoped to `:is(.ut-theme *, .ut-theme)`) apply even
 * though the portal escapes the layout's `.ut-theme` wrapper — this is the
 * rule that keeps portalled dropdowns styled (see DESIGN_SYSTEM.md §1).
 *
 * Visuals come from the existing `.aero-card-menu*` classes; this component
 * adds no new CSS, it just consolidates the three hand-rolled copies (Sites
 * cards, User Testing rows, version history rows) behind one API.
 *
 * Items: `[{ label, onClick, danger, disabled, title, key, desc }]`; `desc`
 * (optional) is a short second line under the label, and `separator: true`
 * draws a hairline above the item (the demo's "Test this site"). Each item's
 * `onClick` fires the action; the menu closes itself and stops the click from
 * bubbling to an enclosing clickable card/row. A top-level `busy` flag
 * disables every item while an action is in flight.
 */
import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

function AeroCardMenu({ items = [], busy = false, ariaLabel = 'More actions', className = '' }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const menuRef = useRef(null);
  const popRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const handleOutside = (e) => {
      // The pop lives in a portal, so check both the kebab wrapper and the
      // portal content before treating a mousedown as outside.
      if (
        menuRef.current && !menuRef.current.contains(e.target) &&
        popRef.current && !popRef.current.contains(e.target)
      ) {
        setMenuOpen(false);
      }
    };
    // The portal is positioned from a one-time rect, so close on scroll or
    // resize rather than leaving the menu floating at a stale position.
    const handleReposition = () => setMenuOpen(false);
    document.addEventListener('mousedown', handleOutside);
    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
    };
  }, [menuOpen]);

  const handleMenuToggle = (e) => {
    e.stopPropagation();
    if (!menuOpen) {
      const rect = e.currentTarget.getBoundingClientRect();
      setMenuPos({ top: rect.bottom + 4, left: rect.right });
    }
    setMenuOpen((v) => !v);
  };

  // Wrap a menu action so it closes the popup and never bubbles to the card.
  const runItem = (item) => (e) => {
    e.stopPropagation();
    setMenuOpen(false);
    item.onClick?.();
  };

  return (
    <div className={`aero-card-menu${className ? ` ${className}` : ''}`} ref={menuRef}>
      <button
        type="button"
        className="aero-btn aero-btn-ghost aero-btn-icon aero-card-menu-btn"
        onClick={handleMenuToggle}
        title={ariaLabel}
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
      >
        <span className="aero-kebab">•••</span>
      </button>
      {menuOpen &&
        ReactDOM.createPortal(
          <div
            className="aero-card-menu-pop aero-card-menu-pop-fixed ut-theme"
            role="menu"
            ref={popRef}
            style={{ top: menuPos.top, left: menuPos.left }}
          >
            {items.map((item, i) => (
              <button
                key={item.key || item.label || i}
                type="button"
                role="menuitem"
                className={[item.danger ? 'danger' : '', item.desc ? 'has-desc' : '', item.separator ? 'sep' : ''].filter(Boolean).join(' ') || undefined}
                disabled={busy || item.disabled}
                title={item.title}
                onClick={runItem(item)}
              >
                {item.label}
                {item.desc && <span className="aero-card-menu-desc">{item.desc}</span>}
              </button>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
}

export default AeroCardMenu;
